/**
 * Standardized Invalidation Helpers
 *
 * Thin delegators around `getDependencies(entityType, matterId, entityId?)`.
 *
 * The canonical source of truth for *what* gets invalidated per entity is
 * `lib/services/query-dependencies.ts`. To add or change a key, edit that file
 * — these helpers automatically pick up the new shape.
 *
 * @see lib/services/query-dependencies.ts
 * @see lib/query-keys/CLAUDE.md (single-source rule)
 */

import { QueryClient, QueryKey } from '@tanstack/react-query';
import { getDependencies } from '@/lib/services/query-dependencies';

async function invalidateAll(
  queryClient: QueryClient,
  keys: QueryKey[]
): Promise<void> {
  await Promise.all(
    keys.map((key) =>
      queryClient.invalidateQueries({ queryKey: key, exact: false })
    )
  );
}

/**
 * Invalidate after correspondence is sent (notification, follow-up,
 * collection request).
 *
 * Sending changes inventory/institution status, unlocks tasks, and updates
 * discovery / redemption tab state.
 */
export async function invalidateAfterCorrespondenceSent(
  queryClient: QueryClient,
  matterId: number
): Promise<void> {
  await invalidateAll(queryClient, getDependencies('correspondence', matterId));
}

/**
 * Invalidate after inventory item changes (status, valuation, holding edits).
 */
export async function invalidateAfterInventoryUpdate(
  queryClient: QueryClient,
  matterId: number,
  itemId?: number
): Promise<void> {
  await invalidateAll(
    queryClient,
    getDependencies('inventory-item', matterId, itemId)
  );
}

/**
 * Invalidate after matter-level fields change (status, dates, advertisedAt,
 * isGrantLodged, etc.).
 */
export async function invalidateAfterMatterUpdate(
  queryClient: QueryClient,
  matterId: number
): Promise<void> {
  await invalidateAll(queryClient, getDependencies('matter', matterId));
}

/**
 * Invalidate after distribution declaration update.
 */
export async function invalidateAfterDeclarationUpdate(
  queryClient: QueryClient,
  matterId: number,
  declarationId?: number
): Promise<void> {
  await invalidateAll(
    queryClient,
    getDependencies('declaration', matterId, declarationId)
  );
}

/**
 * Invalidate after distribution update.
 */
export async function invalidateAfterDistributionUpdate(
  queryClient: QueryClient,
  matterId: number,
  distributionId?: number
): Promise<void> {
  await invalidateAll(
    queryClient,
    getDependencies('distribution', matterId, distributionId)
  );
}

/**
 * Invalidate after entitlement / entitlement-class update.
 *
 * Use this in place of bespoke `invalidateAll` helpers in
 * `lib/services/entitlements/hooks.ts`.
 */
export async function invalidateAfterEntitlementUpdate(
  queryClient: QueryClient,
  matterId: number,
  entitlementId?: number
): Promise<void> {
  await invalidateAll(
    queryClient,
    getDependencies('entitlement', matterId, entitlementId)
  );
}

/**
 * Invalidate after AI Scribe recording **import** (apply changes).
 *
 * Importing extracts inventory / contacts into the matter, so the surface is
 * cross-domain. For metadata-only mutations (rename, dismiss, retry, reanalyse)
 * use `invalidateAfterRecordingUpdate` instead.
 */
export async function invalidateAfterRecordingImport(
  queryClient: QueryClient,
  matterId: number,
  recordingId?: number
): Promise<void> {
  await invalidateAll(
    queryClient,
    getDependencies('recording', matterId, recordingId)
  );
}

/**
 * Invalidate after AI Scribe recording **metadata** mutation
 * (rename / dismiss / undismiss / retry / reanalyse / delete).
 *
 * Narrow scope — does not cross domains.
 */
export async function invalidateAfterRecordingUpdate(
  queryClient: QueryClient,
  matterId: number,
  recordingId?: number
): Promise<void> {
  await invalidateAll(
    queryClient,
    getDependencies('recording-meta', matterId, recordingId)
  );
}

/**
 * Invalidate after an institution payout is recorded or reversed (LF-3250).
 *
 * Routes through the canonical `inventory-item` dependency set so all the
 * caches that depend on per-item status / realised values refresh in
 * lockstep (inventory list, redemption tab, distribution planning,
 * entitlement waterfall, matter institutions, tasks). Additionally
 * invalidates the payout list and detail keys for the institution.
 */
export async function invalidateAfterInstitutionPayout(
  queryClient: QueryClient,
  matterId: number,
  matterInstitutionId?: number
): Promise<void> {
  const keys: QueryKey[] = [
    ...getDependencies('inventory-item', matterId),
    ['matter-institution-payouts', 'list', matterId],
    ['matter-institution-payouts', 'detail', matterId]
  ];
  if (matterInstitutionId != null) {
    keys.push([
      'matter-institution-payouts',
      'list',
      matterId,
      matterInstitutionId
    ]);
  }
  await invalidateAll(queryClient, keys);
}
